// Hybrid session manager. When an SDK + active workspace is reachable
// (`el.sdk.ai`), sessions are SERVER-BACKED — per-member workspace conversation
// history surfaced via sdk.ai.{list,get,create,delete}Conversation. When no SDK
// is present (standalone simone, which talks to a local ws bridge), every
// server path no-ops/throws and we fall through to the local in-memory behavior
// below: sessions live in root state and give the user "New chat" + history
// switch within a panel lifetime.

import { activeWorkspaceId } from '../scopedClient.js'
import { toolOutcome, toolFailure } from './toolOutcome.js'
import { toolEmbedCard, flyPageEmbeds } from './toolEmbed.js'
import { repeatsNote } from './callNote.js'
import { stripAnswerTrailers } from './answerTrailers.js'
import { sanitizeAssistantProtocolText } from './protocolText.js'
import {
  threadPointerKey,
  readThreadPointer,
  writeThreadPointer,
  clearThreadPointer,
  needsInterruptedNotice,
  INTERRUPTED_TURN_NOTICE
} from './threadPointer.js'
import { pendingClientCallOffer } from './pendingClientCall.js'
// Bare shared-library import, not a deep aiService.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0). Deferred-call
// wrappers, not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
import { shellGetAiService } from '../shellSharedDelegates.js'
const resolveAiService = (...args) => _wsShared.functions.resolveAiService(...args)

// Resolve the AI service that actually owns the conversation API (list / get /
// create / delete / patchConversation). In the WORKSPACE that's the shared
// singleton getAiService() — the same instance the send path uses; `el.sdk.ai`
// is a DIFFERENT stub WITHOUT those methods, so every server-backed session path
// (list, create, switch, delete, star) silently fell through to local. That's
// the real reason previous conversations never loaded and stars never persisted.
// Prefer the shared service, validate it exposes the API, and fall back to
// el.sdk.ai for the standalone-vendored case.
const _ai = (el) => {
  try {
    const shared = typeof shellGetAiService === 'function' ? shellGetAiService() : null
    if (shared && typeof shared.listConversations === 'function') return shared
  } catch (_) {
    /* fall through */
  }
  try {
    const sdk = el && el.sdk
    const ai = sdk && (sdk.ai || (sdk.getService && sdk.getService('ai')))
    if (ai && typeof ai.listConversations === 'function') return ai
  } catch (_) {
    /* fall through */
  }
  return null
}

// Map server conversation messages → simone's flat chatMessages shape.
// Server content is an array of typed parts ([{type:'text',text}, …]). Text
// parts join into the `text` field simone renders. tool_call / tool_result
// parts (the agentic loop persists one intermediate message per) FOLD into a
// `tools` timeline attached to the next text-bearing assistant message — the
// same shape the live transport builds from tool.activity frames — so history
// re-renders with the identical ChatToolRow timeline instead of ghost rows.
// Exported (despite the underscore-private naming convention used
// throughout this module) so tickets/opus.md's "credits-exhausted UX" fix —
// restoring `entry.metadata` on history reload — has direct unit coverage
// without standing up the whole hybrid session manager.
// The persisted record's createdAt → the epoch-ms `ts` the live transport
// stamps (Date.now()), so ChatMessage's Meta.Time (`if: !!s.ts`) and
// ChatToolRow's duration (`s.doneTs - s.ts`) survive a history reload
// (AI-CHAT-RESTORED-THREAD-NO-TIMESTAMP-1). Null, never absent, when the
// record carries no parseable time — a key absent at first render is never
// tracked as a dependency (see the agentName seeding note below).
const _messageTs = (m) => {
  const t = m && m.createdAt ? +new Date(m.createdAt) : NaN
  return Number.isFinite(t) ? t : null
}

// A refusal for CREDITS reads as the shell's own sentence, never the server's
// prose (and never its "Before this error, the following actions completed
// successfully…" lead): the rows `_toChatMessages` built go through the shell's
// registered step (functions/creditsRefusal.js creditsRefusalRows) — the copy
// through the live element's polyglot, the way out for the viewer's role. A
// reader that has no element (a bare read) keeps the rows as they are.
const _creditsWorded = (el, root, rows) => {
  try {
    const worded =
      el && typeof el.call === 'function' ? el.call('creditsRefusalRows', rows, root) : null
    return Array.isArray(worded) ? worded : rows
  } catch (_) {
    return rows
  }
}

// A QUESTION ITS TURN LEFT (F1, dev 2026-10-28): a stop, or a turn that
// failed or ended, closed the waiting card and posted a not-run result for
// it (the transport's _endWaitingCards: `{ ok: false, notRun: true,
// stopped? }`; an older thread's closed page wrote the same shape). Live, its
// row reads "Unanswered question" (`denied`) — never a red ✗ failure, which
// the reload drew, under "Waiting for your choice". A Run / Skip the gate
// declined (`denied`) is the gate's, not the question's (_skippedAtTheGate).
const _askLeftByItsTurn = (row, res) =>
  !!row &&
  (row.tool === 'ask_user' || row.tool === 'confirm_with_user') &&
  !!res &&
  res.ok === false &&
  !res.denied &&
  (!!res.notRun || !!res.stopped || !!res.superseded)

// A STEP THE PERSON SKIPPED (coordinator W3b, 2026-10-29: a reload drew every
// step skipped on Run / Skip as a red ✗ failure). On Skip the transport's gate
// settles `{ ok: false, denied: true, error }` (resolveAiToolApproval; a turn
// that ended while the step waited settles the same shape,
// settleParkedAiToolApprovals); the SDK posts it as it is, and the server
// saves it as the tool_result `payload` (prepareWorkspaceResume, metadata
// kind `workspace_client_action`). Live the row reads `denied` — the quiet ✗
// and "skipped" — and the server's completed frame keeps it so. An approval
// that ran out of time (`notRun`, expireParkedAiToolApproval) is a failed
// row live, with its reason: it stays one.
const _skippedAtTheGate = (res) => !!res && res.ok === false && res.denied === true && !res.notRun

// AN ANSWERED QUESTION KEEPS ITS CARD (Nika 2026-10-28: "colalpese after
// answer"). A reloaded thread draws an ask_user / confirm_with_user card
// FOLDED to its answer: the line the live card folds to, under its step,
// with Change (components/_simone/ChatCard.js). Built from the saved call
// (prompt, options, labels) and its saved result. Never live buttons: that
// turn has its answer. A question a new message closed comes back as its
// one muted line; one its turn left (the row's `denied`) as that line with
// its reason (`_closedBy`: 'stop' when the person stopped the reply, else
// 'end'), as live; one never answered (the page left mid-ask) has no card.
const _askCardFromHistory = (row, res) => {
  const confirmTool = !!row && row.tool === 'confirm_with_user'
  if (!row || (row.tool !== 'ask_user' && !confirmTool)) return null
  const superseded = row.status === 'superseded'
  // A question skipped on Run / Skip (a re-offered one: `denied`) is the
  // gate's: its row reads "skipped", as live, and it never drew a card.
  const left = row.status === 'denied' && !(res && res.denied)
  if (!superseded && !left && (!res || res.ok === false)) return null
  const a = row.args && typeof row.args === 'object' ? row.args : {}
  const options = Array.isArray(a.options) ? a.options : []
  const confirm = confirmTool || a.kind === 'confirm'
  const label = (o) => (o && o.label != null ? String(o.label) : null)
  const card = {
    type: confirm ? 'confirm' : 'choice',
    prompt: String((confirmTool ? a.body : a.prompt) || ''),
    ...(confirm
      ? {
          confirmLabel: confirmTool ? a.confirmText || null : label(options[0]),
          cancelLabel: confirmTool ? a.cancelText || null : label(options[1])
        }
      : { options }),
    _cardId: 'ask-' + row.id,
    _callId: row.id,
    _resolved: true,
    _chosen: null,
    _chosenLabel: null,
    _superseded: superseded || left,
    _closedBy: left ? (res && res.stopped ? 'stop' : 'end') : null,
    _written: false,
    _edit: false
  }
  if (superseded || left) return card
  const edit = typeof res.edit === 'string' ? res.edit.trim() : ''
  if (edit) return { ...card, _chosen: 'edit', _chosenLabel: edit, _edit: true }
  if (confirmTool) {
    if (typeof res.confirmed !== 'boolean') return null
    return { ...card, _chosen: res.confirmed ? 'confirm' : 'cancel' }
  }
  const choice = res.choice != null ? String(res.choice) : ''
  const shown = String(res.label != null ? res.label : choice).trim()
  if (!shown) return null
  return { ...card, _chosen: choice || shown, _chosenLabel: shown, _written: !!res.written }
}

// The Continue chip's own word, in the reader's language — what a thread
// saved before the chip sent the word (it sent the model's resume
// instruction as the member's text, persona round 4, elena) shows under
// "You" on a reload. Null when there is no element to ask.
export const _continueWord = (el) => {
  try {
    const w =
      el && typeof el.call === 'function' ? el.call('polyglot', 'common.action.continue') : null
    return typeof w === 'string' && w ? w : null
  } catch (_) {
    return null
  }
}

// The instruction the Continue chip sent as the member's text before the
// resume became `context.resume` — matched whole, only on a user row.
const _LEGACY_CONTINUE_PROMPT =
  'Continue from where the last turn stopped — the completed actions are already saved; do not redo them.'

// `words.continueWord` (optional) — see _continueWord.
export const _toChatMessages = (messages = [], words = {}) => {
  const out = []
  let pendingTools = []
  let pendingCards = []
  for (const m of messages) {
    if (!m) continue
    const parts = m.content || []
    const toolCall = parts.find((p) => p && p.type === 'tool_call')
    if (toolCall) {
      const payload = toolCall.payload || {}
      // What the model SAID beside this call (core-wl callText, server
      // 84527812: [text before] + tool_call + [text after], in order). Live,
      // those words streamed before the call ran and became its NOTE — the
      // same place here, joined in the saved order; never a second bubble.
      // A call saved alone (older sessions) has no note.
      // No machine trailer: the P0 loop saved `<!--SUGGEST:[…]-->` here, at
      // times split across two text parts — so the parts are stripped as ONE
      // text (a separator that a removed trailer takes with it), then split.
      const beside = stripAnswerTrailers(
        parts
          .filter((p) => p && p.type === 'text' && typeof p.text === 'string' && p.text.trim())
          .map((p) => p.text)
          .join('\u0001')
      )
        .split('\u0001')
        .map((t) => t.trim())
        .filter(Boolean)
        .join('\n')
      pendingTools.push({
        id: String(m.id || m._id || `t-${pendingTools.length}`),
        tool: payload.name || 'tool',
        args: payload.args || null,
        // The step in plain words (server lane ai-ux saves the frame's `label`
        // on the call, 2026-09-30) — ChatToolRow shows it instead of the tool
        // name and its arguments. Null, never absent, on an older saved call:
        // the live transport seeds the same key (simoneAiTransport
        // _patchToolActivity), and a key absent at first render is never
        // tracked.
        label:
          typeof payload.label === 'string' && stripAnswerTrailers(payload.label).trim()
            ? stripAnswerTrailers(payload.label).trim()
            : null,
        ...(beside ? { note: beside } : {}),
        // The tool_call message's own persisted time = the step's start instant.
        ts: _messageTs(m),
        status: 'done'
      })
      // Display cards persist in the tool_call args — restore them so a
      // reloaded thread shows the same cards the live turn rendered.
      // A question card (ask_user, confirm_with_user) never comes back with
      // its buttons — they would offer dead choices; an answered one comes
      // back folded to its answer, at its result (_askCardFromHistory).
      if (payload.name === 'render_cards' && Array.isArray(payload.args?.cards)) {
        pendingCards = pendingCards.concat(payload.args.cards)
      }
      continue
    }
    const toolResult = parts.find((p) => p && p.type === 'tool_result')
    if (toolResult) {
      const last = pendingTools[pendingTools.length - 1]
      if (last) {
        if (toolResult.payload && toolResult.payload.ok === false) last.status = 'error'
        // A step a new message closed while it waited (server
        // closeDanglingClientCall, reason `new_message`) reads as the live row
        // did: superseded, with "Run it now" — never as a failure.
        if (
          (toolResult.payload && toolResult.payload.reason === 'new_message') ||
          (m.metadata && m.metadata.reason === 'new_message')
        ) {
          last.status = 'superseded'
        } else if (_askLeftByItsTurn(last, toolResult.payload)) {
          // A question its turn left reads as the live row did: unanswered.
          last.status = 'denied'
        } else if (_skippedAtTheGate(toolResult.payload)) {
          // A step the person skipped reads as the live row did: skipped.
          last.status = 'denied'
        }
        const notRun = !!toolResult.payload?.notRun
        // …and a step that never ran is marked so, as live (expireParkedAiToolApproval):
        // its row shows no words written before the call (F3).
        if (notRun && toolResult.payload.ok === false) last.notRun = true
        const summary = stripAnswerTrailers(String(toolResult.text || ''))
        // A SERVER tool's refusal (every kind but the browser's own
        // workspace_client_action): line 1 is the member's, line 2+ and the
        // payload are the MODEL's English (toolFailureLine.js on the server).
        // The live frame keeps line 1 as summary and reason; so does a reload
        // (persona round 4, giorgi: "Nothing was saved — … Fix ALL of them").
        const kind = m.metadata && m.metadata.kind
        const serverRefusal =
          !notRun &&
          toolResult.payload &&
          toolResult.payload.ok === false &&
          typeof kind === 'string' &&
          kind !== 'workspace_client_action'
        const firstLine = summary.split('\n')[0].trim()
        // A closed page's result also carries retry instructions for the model.
        // Restore only its plain status line, as the live completed frame does.
        last.summary = (notRun || serverRefusal ? firstLine : summary).slice(0, 300)
        // The tool_result message's time = the step's done instant — with `ts`
        // above it re-renders ChatToolRow's duration tag after a reload.
        last.doneTs = _messageTs(m)
        // AI-301 — restore the structured created-item outcome the live turn
        // stamped (simoneAiTransport onToolCall), so ChatToolRow's link to the
        // created item survives a history reload, not just the live turn.
        const created = toolOutcome(toolResult.payload)
        if (created) last.created = created
        // An answered question (ask_user): its saved result is the card's own
        // return value ({ ok, choice, label }), so the row names the pick again.
        // An Edit is no pick: its row says "You answered", its card the words.
        const res = toolResult.payload
        if (last.tool === 'ask_user' && res && res.ok !== false && !res.edit) {
          const pick = String(res.label != null ? res.label : res.choice != null ? res.choice : '')
          if (pick.trim()) last.answer = pick.trim()
        }
        // …and its card comes back folded to that answer, under its step.
        const askCard = _askCardFromHistory(last, res)
        if (askCard) pendingCards.push(askCard)
        // …and a refusal's reason (toolFailure), so a reloaded ✗ still says why.
        const failure = notRun
          ? null
          : serverRefusal
            ? firstLine || null
            : toolFailure(toolResult.payload)
        if (failure) last.failure = failure
      }
      // An EMBED tool's live block (embed_records / embed_widget): the SAME card
      // the live turn drew from its completed frame (_simone/toolEmbed.js), on
      // the answer this tool belongs to. A result without `embed` (an older
      // session, any other tool, a refusal) restores nothing.
      const embedCard = toolEmbedCard(toolResult.payload)
      if (embedCard) {
        pendingCards.push({
          ...embedCard,
          _embedFor: last ? last.id : String(m.id || m._id || '')
        })
      }
      continue
    }
    let text = parts
      .filter((p) => p && p.type === 'text')
      .map((p) => p.text)
      .join('')
    // No machine trailer reaches a rendered answer (saved P0 threads hold one).
    if (m.role === 'assistant') text = stripAnswerTrailers(text)
    // The builder's own notes never read as the person's words: the transport
    // prepends `[user is pointing at: …]` lines to the TEXT it sends so the
    // model sees them, and threads saved before CORE builder-r2 also hold the
    // `[editing multi-page app …]` block it used to prepend; the server saved
    // the turn as sent — the live bubble never showed them, so neither does a
    // reload (sam, round 1b: "[editing multi-page app… do NOT create_app
    // again.]" under "You").
    if (m.role === 'user') {
      text = text
        .replace(/^(?:\[user is pointing at: [^\n]*\]\n)+\n/, '')
        .replace(/^\[editing multi-page app [\s\S]*?do NOT create_app again\.\]\n\n/, '')
      if (words && words.continueWord && text.trim() === _LEGACY_CONTINUE_PROMPT) {
        text = words.continueWord
      }
    }
    // AI-302 — the history-reload gate: a PRE-AI-203 conversation may hold a
    // persisted assistant answer that IS tool-call protocol text (the
    // measured 3-of-8 leak). Never render it as prose; keep the raw for
    // diagnosis on the entry's metadata below.
    let protocolLeak = null
    if (m.role === 'assistant') {
      const leakCheck = sanitizeAssistantProtocolText(text)
      if (leakCheck.leaked) {
        text = leakCheck.text
        protocolLeak = leakCheck.raw
      }
    }
    const entry = { id: String(m.id || m._id || ''), role: m.role, text, ts: _messageTs(m) }
    if (protocolLeak != null) {
      entry.metadata = { protocolLeak: true, rawProtocolText: protocolLeak }
    }
    // AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1 — the author is a property of the
    // MESSAGE. The server persists the answering agent's display name on the
    // assistant message metadata; restore it so a reloaded transcript keeps
    // the correspondent that actually wrote each reply, in a thread whose
    // routing target changed part-way through. Seeded to null on an
    // agent-less reply for the same reason `tools`/`cards` are seeded: a key
    // absent at first render is never tracked as a dependency, so ChatMessage's
    // Author factory would not re-fire if the row were later patched.
    if (m.role === 'assistant') entry.agentName = m.metadata?.agentName || null
    // Follow-up suggestions persist on the assistant message's metadata —
    // restore them so a reloaded thread's NEWEST reply regrows its buttons
    // (ChatMessage gates rendering to the last row, so older rows carry the
    // data invisibly).
    if (
      m.role === 'assistant' &&
      Array.isArray(m.metadata?.suggestions) &&
      m.metadata.suggestions.length
    ) {
      entry.suggestions = m.metadata.suggestions
    }
    // GROUNDING (spec §4.2–4.3) — the refs the answer cited and its computed
    // confidence verdict, persisted on the assistant message's metadata by the
    // server. Restored for the same reason `suggestions` right above is: the
    // key must EXIST on the entry at first render or ChatMessage's Sources /
    // Confidence factories never track it as a dependency.
    //
    // ⚠️ BOTH are seeded UNCONDITIONALLY on every assistant row, empty/null
    // included — unlike `suggestions`, which is only attached when the server
    // has some. A reloaded answer with no confidence must render the muted
    // "Unscored" pill, and a row whose key is simply absent renders nothing at
    // all: "the model did not say" and "the model was confident" would look
    // identical. Spec §4.3 — confidence is ALWAYS present on an assistant row.
    if (m.role === 'assistant') {
      entry.sources = Array.isArray(m.metadata?.sources) ? m.metadata.sources : []
      entry.confidence =
        m.metadata?.confidence && typeof m.metadata.confidence === 'object'
          ? m.metadata.confidence
          : null
      // The advice level (vault spec 06 §7), seeded for the same reason:
      // null on every row a workspace does not grade, the server's bag
      // where it does. ChatMessage renders nothing for null.
      entry.level =
        m.metadata?.level && typeof m.metadata.level === 'object' ? m.metadata.level : null
    }
    // Structured error signal (tickets/opus.md "credits-exhausted UX") —
    // restore it on reload too, not just on the live turn, so a reloaded
    // thread's out-of-credits row still carries the metadata-key check
    // ChatMessage's onInit/onUpdate hook reads (`s.metadata.kind==='error'`).
    // Previously dropped entirely on history reload (only `suggestions` was
    // pulled off `m.metadata` here) — a page refresh silently lost the
    // signal even though the server persisted it correctly.
    if (m.role === 'assistant' && m.metadata?.kind === 'error') {
      // Spread-merged — an AI-302 protocol-leak stamp set at entry
      // construction must survive this restore, not be clobbered by it.
      entry.metadata = {
        ...(entry.metadata || {}),
        kind: m.metadata.kind,
        errorCode: m.metadata.errorCode,
        errorMessage: m.metadata.errorMessage,
        retryable: m.metadata.retryable,
        creditsExhausted: !!m.metadata.creditsExhausted
      }
      // The refusal's own figures, when the server states them structurally.
      // Only copied when present, so a row without them keeps the shape above.
      if (m.metadata.creditsNeeded != null) entry.metadata.creditsNeeded = m.metadata.creditsNeeded
      // An unkept-promise turn (`TURN_ENDED_ON_PROMISE`) carries
      // `memberText: true`: its text is the model's words plus the server's
      // stop line in the member's language, and ChatMessage shows it as
      // written. Dropped here, a reloaded row fell back to the generic line.
      if (m.metadata.memberText === true) entry.metadata.memberText = true
      if (m.metadata.creditsAvailable != null) {
        entry.metadata.creditsAvailable = m.metadata.creditsAvailable
      }
    }
    if (m.role === 'assistant' && pendingTools.length) {
      // The answer REPEATS its last call's note (the model said it beside the
      // call, then again as its answer — _simone/callNote.js): once, as the
      // answer, exactly as the live turn shows it.
      const endCall = pendingTools[pendingTools.length - 1]
      if (endCall && repeatsNote(entry.text, endCall.note)) {
        pendingTools[pendingTools.length - 1] = { ...endCall, note: null }
      }
      entry.tools = pendingTools
      pendingTools = []
    }
    // A FLY PAGE's answer (`metadata.flyPage`, Fly mode): its sections are
    // this answer's live cards and its word blocks its words (toolEmbed.js
    // flyPageEmbeds) — a Fly thread opened as a thread showed the openings
    // only (Nika 2026-10-18).
    if (m.role === 'assistant' && m.metadata && m.metadata.flyPage) {
      const fly = flyPageEmbeds(m.metadata.flyPage)
      if (fly.words) entry.text = [entry.text, fly.words].filter(Boolean).join('\n\n')
      if (fly.cards.length) pendingCards = pendingCards.concat(fly.cards)
    }
    // A SETUP NOTE (server lane S1, the workspace's first thread: `metadata:
    // { kind: 'note', note: 'plan' | 'summary', source: 'setup', plan? }`,
    // no AI turn): its kind rides the row, so every renderer can tell a note
    // from an answer, and the plan rides with it — the shell draws the plan
    // card in its own language (functions/workspaceSetup.js). The plan note
    // has no words of its own (`text: ''`: the shared row draws nothing for
    // it); the summary note's words are Fly's, shown as written.
    if (m.role === 'assistant' && m.metadata && m.metadata.kind === 'note') {
      const plan = m.metadata.plan
      entry.metadata = {
        ...(entry.metadata || {}),
        kind: 'note',
        note: typeof m.metadata.note === 'string' ? m.metadata.note : '',
        source: typeof m.metadata.source === 'string' ? m.metadata.source : '',
        plan: plan && typeof plan === 'object' && !Array.isArray(plan) ? plan : null
      }
    }
    if (m.role === 'assistant' && pendingCards.length) {
      entry.cards = pendingCards
      pendingCards = []
    }
    out.push(entry)
  }
  // A conversation can END on tool rows with no answer after them — most
  // importantly when the loop is still SUSPENDED on a client tool this browser
  // never ran. Those trailing rows had no later assistant message to fold onto
  // and were dropped outright, so a reload came back with no timeline row for
  // the pending call and nothing to re-offer it on
  // (SIMONE-APPROVAL-REOFFER-PENDING-CLIENT-CALL-UI-1).
  if (pendingTools.length || pendingCards.length) {
    // The turn ENDED on its call: the words the model said beside that call
    // are its answer — shown once, as the answer (as live), when the row they
    // land on has no answer of its own; otherwise they stay the call's note.
    const last = out[out.length - 1]
    const lands = last && last.role === 'assistant' ? String(last.text || '').trim() : ''
    const endCall = pendingTools[pendingTools.length - 1]
    const endWords = !lands && endCall && typeof endCall.note === 'string' ? endCall.note : ''
    if (endWords) pendingTools[pendingTools.length - 1] = { ...endCall, note: null }
    if (last && last.role === 'assistant') {
      if (pendingTools.length) last.tools = (last.tools || []).concat(pendingTools)
      if (pendingCards.length) last.cards = (last.cards || []).concat(pendingCards)
      if (endWords) last.text = endWords
    } else {
      out.push({
        id: `a-tools-${(pendingTools[0] && pendingTools[0].id) || Date.now()}`,
        role: 'assistant',
        text: endWords,
        // No persisted answer message exists to carry a time — inherit the
        // first trailing tool's start instant so the row is not timeless.
        ts: pendingTools[0]?.ts ?? null,
        // Seed every key ChatMessage's factories read. A key that is ABSENT at
        // the row's first render is never tracked as a dependency, so the row
        // would not repaint when the approval state later lands on it — the
        // same trap the live placeholder's tools/cards/suggestions seeding
        // avoids (see simoneAiTransport.js).
        tools: pendingTools,
        cards: pendingCards,
        suggestions: [],
        // No text, so no agent ever wrote it — but the key is seeded for the
        // same dependency-tracking reason as the three above.
        agentName: null
      })
    }
  }
  // The first thread's two notes — the plan, then the summary — are ONE row:
  // the summary's words, carrying the plan. A hidden plan row before the
  // summary would group the summary under it (Chat.js `_grouped`: no author,
  // no time), so the summary would not read as the thread's first answer. A
  // plan with no summary stays its own row, with no words.
  for (let i = 0; i < out.length - 1; i++) {
    const a = out[i].metadata
    const b = out[i + 1].metadata
    if (
      a &&
      a.kind === 'note' &&
      a.note === 'plan' &&
      a.plan &&
      b &&
      b.kind === 'note' &&
      b.note === 'summary' &&
      !b.plan
    ) {
      out[i + 1] = { ...out[i + 1], metadata: { ...b, plan: a.plan } }
      out.splice(i, 1)
      break
    }
  }
  return out
}

const newSessionId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`

const titleFromMessages = (el, msgs) => {
  const fallback = () => el.call('polyglot', 'workspace.simone.newChat')
  if (!msgs || !msgs.length) return fallback()
  const first = msgs.find((m) => m.role === 'user')
  const txt = (first && first.text) || ''
  return txt.slice(0, 60) + (txt.length > 60 ? '…' : '') || fallback()
}

// Resolve the namespace prefix + project scope for a chat context. The Chat
// callers pass `scope` explicitly ('general' | 'addapp'); /add-app threads live
// under _ax_* root keys and are project-scoped to the active extension
// (root._addAppProjectId), so they never collide with the general drawer/composer.
const _scope = (root, scope) => {
  const addapp = scope === 'addapp'
  const projectId = addapp ? (root && root._addAppProjectId) || null : null
  // dashboard = the inline-thread surface on / (its own thread, _db_*); like the
  // general drawer it is workspace-scoped (no projectId), just key-namespaced.
  const k = addapp ? '_ax_' : scope === 'dashboard' ? '_db_' : ''
  return { k, projectId, opts: projectId ? { projectId } : {} }
}

// Pin the AI service to the active in-org workspace before ANY server conversation
// call (list / get / create). General threads need activeWorkspaceId set or
// _conversationBase() has no base → 404; add-app passes opts.projectId per-call
// instead, so it skips this. Always uses the drift-corrected workspace
// (activeWorkspaceId → root.workspace.id) so list/get/create AGREE even right after
// an org switch — otherwise a thread created/loaded under a stale workspace silently
// 404s or lands in the wrong org. Best-effort: the call may still resolve a cached ws.
const pinWorkspace = (ai, root, projectId) => {
  if (projectId) return
  try {
    const wsId = activeWorkspaceId(root)
    if (wsId && ai && typeof ai.updateContext === 'function')
      ai.updateContext({ activeWorkspaceId: wsId })
  } catch (_) {
    /* best-effort */
  }
}

// ── Reload survival (SIMONE-INFLIGHT-TURN-NO-PERSISTENCE-1) ────────────────
// `activeSessionId` is in-memory root state, so every reload — F5, a parcel HMR
// refresh, a crash, the AppOrgSwitcher org-switch fallback — forgot WHICH thread
// was open and the panel came back empty while the server still held the whole
// conversation. The pointer below is the only thing persisted; the transcript is
// re-read from the server (`getConversation`), which stays the single source of
// truth. `./threadPointer.js` holds the policy (key, TTL, notice) and is
// store-injected, so a host with no storage degrades to today's behavior.
const _store = () => {
  try {
    return (typeof globalThis !== 'undefined' && globalThis.localStorage) || null
  } catch (_) {
    // Storage access throws outright under some privacy settings.
    return null
  }
}

// One pointer per tenant AND per correspondent: add-app threads belong to their
// extension's project, general/dashboard to the active workspace. Keying by
// tenant is what stops a pointer restoring another org's conversation after a
// switch; keying by `_threadAgentId` is answer C on
// AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1 — a named agent keeps its own thread,
// so entering from its card resumes ITS history, never whatever was last open.
//
// `_threadAgentId` is the agent that OWNS the open thread. It is NOT
// `_targetAgentId`, which is where the NEXT message goes: an @-mention aims a
// message at an agent without moving the user out of the thread they are
// typing in, and moving the pointer under them would file that thread as the
// agent's.
const _pointerKey = (root, scope, projectId) =>
  threadPointerKey(scope, projectId || activeWorkspaceId(root), root && root._threadAgentId)

// The conversation the ADDRESS BAR names for the general thread — `?ai=<id>`,
// the main thread's URL (functions/assistantThreadUrl.js) — or null. The
// add-app and dashboard threads have no URL of their own; a host without the
// function (standalone simone, the preview) has no thread URL at all: el.call
// of a name no context registers answers undefined (smbls utils call()).
const _linkedThreadId = (el, scope) => {
  if (!el || scope === 'addapp' || scope === 'dashboard') return null
  return el.call('assistantThreadLinkId') || null
}

// How many messages the chat holds — a send appends, so a change in this
// count while restoreActiveThread reads means the member acted meanwhile.
const _chatLength = (list) => (Array.isArray(list) ? list.length : 0)

const _rememberThread = (root, scope, projectId, id, inflight) => {
  const store = _store()
  if (!store) return
  const key = _pointerKey(root, scope, projectId)
  if (!id) clearThreadPointer(store, key)
  else writeThreadPointer(store, key, id, inflight)
}

// Snapshot the live message buffer into the active session before mutating.
// `k` is the scope's key prefix ('' general | '_ax_' add-app).
const shellSnapshot = (el, root, k = '') => {
  const id = root[k + 'activeSessionId']
  if (!id) return
  const sessions = { ...(root[k + 'sessions'] || {}) }
  const cur = sessions[id] || { id, ts: Date.now() }
  const msgs = root[k + 'chatMessages'] || []
  // A thread nobody asked in yet keeps the title it came with (the first
  // workspace thread's is the company's name — its notes hold no question).
  const asked = msgs.some((m) => m && m.role === 'user')
  sessions[id] = {
    ...cur,
    id,
    title: asked || !cur.title ? titleFromMessages(el, msgs) : cur.title,
    messages: msgs,
    ts: cur.ts || Date.now()
  }
  root.update({ [k + 'sessions']: sessions })
}

export const ensureActiveSession = function () {
  const root = this.state.root
  if (root.activeSessionId) return
  const id = newSessionId()
  const sessions = { ...(root.sessions || {}) }
  sessions[id] = {
    id,
    title: this.call('polyglot', 'workspace.simone.newChat'),
    messages: [],
    ts: Date.now()
  }
  root.update({ sessions, activeSessionId: id })
}

// Server-backed hydration. When an SDK + active workspace is reachable, replace
// `root.sessions` with the member's persisted conversation history so the
// Sessions ▾ dropdown reflects real server state. Preserves any already-loaded
// message buffers for ids we've seen. No-op (and silent) without an SDK or
// active workspace — standalone simone keeps its local sessions untouched.
export const hydrateServerSessions = async function (scope) {
  const el = this
  const root = el.state.root
  const { k, projectId, opts } = _scope(root, scope)
  // add-app with no extension selected yet → nothing to hydrate (the thread is
  // created on the first message; the sidebar selecting an extension hydrates it).
  if (scope === 'addapp' && !projectId) return
  // Resolve the real AI service (with the conversation API). _ai prefers the
  // cached getAiService() singleton; during the boot window it's still null, so
  // await resolution here so PREVIOUS chats load on first open (not only after a
  // turn).
  let ai = _ai(el)
  if (!ai) {
    try {
      ai = await resolveAiService(el && el.sdk)
    } catch (_) {
      return
    }
  }
  if (!ai || typeof ai.listConversations !== 'function') return
  // Scope the SDK. add-app passes opts.projectId per-call (routes to
  // /projects/:id/conversations WITHOUT changing the global SDK context, so it
  // can't leak into the general drawer). General sets the active workspace before
  // listing — hydrate runs on load, before the send handler sets it, otherwise
  // _conversationBase() throws "no active workspace" → silent 0 chats.
  pinWorkspace(ai, root, projectId)
  try {
    const list = await ai.listConversations(opts)
    if (!Array.isArray(list)) return
    const prev = root[k + 'sessions'] || {}
    const sessions = {}
    for (const t of list) {
      const id = t && (t.conversationId || t.id)
      if (!id) continue
      sessions[id] = {
        id,
        title: (t && t.title) || el.call('polyglot', 'workspace.simone.newChat'),
        ts: t && t.lastMessageAt ? +new Date(t.lastMessageAt) : Date.now(),
        pinned: !!(t && t.pinned),
        messages: (prev[id] && prev[id].messages) || []
      }
    }
    root.update({ [k + 'sessions']: sessions })
  } catch (err) {
    // Don't fail silently — this masked the "previous conversations don't load"
    // bug. Keep local sessions, but surface the reason.
    if (typeof console !== 'undefined')
      console.debug(
        '[simone] hydrateServerSessions: listConversations failed —',
        err && err.message
      )
  }
}

// Scope-aware: `scope` ('general'|'addapp') selects the key prefix (''|'_ax_')
// and, for add-app, the project the new conversation is created under. chatInput
// /pickedElements are general-only keys (no _ax_ variant) — cleared only there.
export const newSession = async function (scope) {
  const el = this
  const root = el.state.root
  const { k, projectId, opts } = _scope(root, scope)
  // Parked approvals from the outgoing thread must settle (as declined) —
  // orphaning them strands the server-side turn forever.
  try {
    el.call(
      'settleParkedAiToolApprovals',
      'The conversation was switched before this action was approved.'
    )
  } catch (_) {}
  const reset = {
    [k + 'chatMessages']: [],
    [k + 'chatTyping']: false,
    [k + 'currentAssistantId']: null,
    [k + 'showSessionMenu']: false
  }
  if (!k) {
    reset.chatInput = ''
    reset.pickedElements = []
  }
  // LAZY thread — do NOT create a server conversation here. Eager-creating one
  // persisted an EMPTY thread in the DB whenever the user opened a new chat but
  // never sent. Instead use a LOCAL placeholder ('s-…' id); the FIRST message
  // lazily creates the real server conversation (the transport dispatches with
  // conversationId=null for an 's-…'/absent id, so the server creates it WITH that
  // message, and onDone re-points activeSessionId to the real id). Net: only
  // non-empty threads ever reach the database.
  shellSnapshot(this, root, k)
  const id = newSessionId()
  root.update({
    [k + 'sessions']: {
      ...(root[k + 'sessions'] || {}),
      [id]: {
        id,
        title: this.call('polyglot', 'workspace.simone.newChat'),
        messages: [],
        ts: Date.now()
      }
    },
    [k + 'activeSessionId']: id,
    ...reset
  })
  // A local placeholder names no server conversation, so the pointer is dropped
  // rather than pointed at something a reload could not load back.
  _rememberThread(root, scope, projectId, id, false)
  // Tell the bridge to spin up a fresh claude subprocess for this session.
  if (typeof window !== 'undefined' && typeof window.__simoneSendOverBridge === 'function') {
    try {
      window.__simoneSendOverBridge({ type: 'reset' })
    } catch (_) {}
  }
}

// Open a named agent's own thread — the one entry point every agent card goes
// through (AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1, answer C, Nika 2026-08-30:
// "One persistent thread PER AGENT, resumed each time you enter from that agent
// card"). Pass null to go back to the general assistant's thread.
//
// Measured on production 2026-08-30 06:47 +0400, before this existed: "Talk to
// QA" only wrote `_targetAgentId`, so the drawer kept whatever thread was open
// — its title and its messages — and merely aimed the next message at QA. A
// named agent is a separate correspondent; its history belongs to it.
//
// Resume-or-start, and it loses nothing either way: the outgoing thread is
// snapshotted into `root.sessions` first, then the incoming correspondent's
// remembered thread is restored, and only an agent with NO remembered thread
// gets a fresh empty one.
export const openAgentThread = async function (agent, scope) {
  const el = this
  const root = el.state.root
  const agentId = (agent && (agent.userId || agent.id)) || null
  const agentName = (agent && (agent.name || agent.id)) || null
  const { k } = _scope(root, scope)

  // Already this correspondent's thread — re-aim the routing target and stop.
  // Reloading here would discard a half-typed draft and flicker the panel for
  // no change at all.
  if ((root._threadAgentId || null) === agentId) {
    root.update({ _targetAgentId: agentId, _targetAgentName: agentName })
    return
  }

  // Parked approvals from the outgoing thread must settle (as declined) —
  // orphaning them strands the server-side turn forever. Same reason as
  // `newSession`.
  try {
    el.call(
      'settleParkedAiToolApprovals',
      'The conversation was switched before this action was approved.'
    )
  } catch (_) {}

  shellSnapshot(this, root, k)
  // The agent keys move BEFORE the restore, because `_pointerKey` reads
  // `_threadAgentId` to decide which pointer to look up.
  root.update({
    _threadAgentId: agentId,
    _threadAgentName: agentName,
    _targetAgentId: agentId,
    _targetAgentName: agentName,
    [k + 'activeSessionId']: null,
    [k + 'chatMessages']: [],
    [k + 'chatTyping']: false,
    [k + 'currentAssistantId']: null,
    [k + 'showSessionMenu']: false
  })

  await restoreActiveThread.call(el, scope)
  // No remembered thread for this correspondent (or it could not be loaded) —
  // start an empty one, so the panel and what it shows agree.
  if (!root[k + 'activeSessionId']) await newSession.call(el, scope)
}

// Rehydrate the thread that was open before the page went away. Called from
// Chat.onCreate right after `hydrateServerSessions` (which only ever loaded the
// thread LIST). No pointer, no storage, no SDK → silent no-op, i.e. exactly the
// pre-fix empty-chat behavior, never an error.
export const restoreActiveThread = async function (scope) {
  const el = this
  const root = el.state.root
  const { k, projectId, opts } = _scope(root, scope)
  // The user already opened a thread on this mount (a fast click, or add-app
  // selecting an extension). Their choice wins over the remembered one.
  if (root[k + 'activeSessionId']) return
  const store = _store()
  if (!store) return
  const key = _pointerKey(root, scope, projectId)
  const pointer = readThreadPointer(store, key)
  if (!pointer) return
  // The address bar names a thread too (a pasted link, a reload, Back /
  // Forward), and that is the person's choice exactly like a click is. The
  // remembered thread loads only when it IS that thread. Any other one stays
  // on the server as it is: loading it first would re-offer its waiting client
  // call below, and the switch to the linked thread would then settle that
  // call as declined before the person ever saw it. Not chosen is not dead —
  // the pointer is kept.
  const linked = _linkedThreadId(el, scope)
  if (linked && linked !== pointer.id) return

  let ai = _ai(el)
  if (!ai) {
    try {
      ai = await resolveAiService(el && el.sdk)
    } catch (_) {
      return
    }
  }
  if (!ai || typeof ai.getConversation !== 'function') return
  pinWorkspace(ai, root, projectId)

  // What the chat holds as the read starts — see the check after the read.
  const heldAtRead = _chatLength(root[k + 'chatMessages'])
  const typingAtRead = !!root[k + 'chatTyping']

  let msgs = null
  let offer = null
  try {
    const data = await ai.getConversation(pointer.id, opts)
    const words = { continueWord: _continueWord(el) }
    msgs = _creditsWorded(el, root, _toChatMessages(data && data.messages, words))
    // The cloud loop may still be SUSPENDED on a client tool this page never
    // ran. The server persists that whole call on the conversation and serves
    // it here, and `.../tool-result` accepts its callId from a brand-new
    // connection — so a fresh page can still complete the turn. Read the offer
    // now; the transport re-offers it below.
    offer = pendingClientCallOffer(data && data.conversation, data && data.messages)
  } catch (err) {
    // Deleted, archived, or belonging to a tenant we can no longer read. A
    // pointer that cannot be loaded is a dead pointer — drop it instead of
    // re-asking the server on every single mount.
    clearThreadPointer(store, key)
    if (typeof console !== 'undefined')
      console.debug('[simone] restoreActiveThread: getConversation failed —', err && err.message)
    return
  }

  // The member chose WHILE the read was in flight — a send on a cold load
  // starts its own session with their message: that choice wins exactly like
  // one made before the read, never the remembered thread laid over it (the
  // first send after a cold load vanished, dev 2026-10-05). Not chosen is not
  // dead — the pointer is kept, as for a linked thread above.
  if (root[k + 'activeSessionId']) return
  // …and a send that has NO id yet is the same choice: it goes with
  // `conversationId: null`, the SDK continues its last conversation, and the
  // turn names the id only when it ENDS (prod check 2026-10-06, DESIGN-
  // COMPOSER-PRIVACY-RESTORE-PROD-LEG-1). A read that lands while that turn
  // runs finds no id — so a message added, or a turn started, during the
  // read wins too. Nothing changed while reading → the remembered thread
  // loads as before.
  if (
    _chatLength(root[k + 'chatMessages']) !== heldAtRead ||
    (!typingAtRead && root[k + 'chatTyping'])
  ) {
    return
  }

  // The page went away mid-turn. The approval gate's parked promise died with
  // it, so anything waiting on Run/Skip never ran — say so, once, rather than
  // letting the user retry a write that may or may not already exist.
  // A live offer makes that notice WRONG: the call is not lost, it is about to
  // be asked again on its own row. Re-offer instead of saying "did not run".
  if (!offer && needsInterruptedNotice(pointer, msgs)) {
    msgs = msgs.concat({
      id: `sys-interrupted-${pointer.ts}`,
      role: 'system',
      text: INTERRUPTED_TURN_NOTICE
    })
  }
  // One-shot: the notice belongs to the reload that earned it, not to every
  // later mount. Settle the flag before the state write so a crash between the
  // two cannot re-warn.
  writeThreadPointer(store, key, pointer.id, false)

  root.update({
    [k + 'activeSessionId']: pointer.id,
    [k + 'chatMessages']: msgs,
    // A turn cannot survive the reload — never come back stuck on "Thinking…".
    [k + 'chatTyping']: false,
    [k + 'currentAssistantId']: null
  })

  // Hand the suspended call to the transport, which owns the authorization
  // gate and the platform runner. Through `el.call`, not an import, for the
  // same reason `settleParkedAiToolApprovals` above goes that way.
  if (offer) {
    try {
      el.call('reofferPendingClientCall', {
        offer,
        conversationId: pointer.id,
        scope,
        opts,
        stateRoot: root,
        // The service that just READ this conversation is the one that owns the
        // conversation API — hand it over rather than let the transport
        // re-resolve and risk the SDK stub that lacks submitToolResult.
        ai
      })
    } catch (_) {
      /* best-effort — the thread still restored; only the resume is lost */
    }
  }
}

// Flip the remembered thread's in-flight flag. The transport calls this at
// dispatch (true) and when the turn settles — onDone or onError (false).
export const markThreadTurn = function (scope, inflight) {
  const root = this.state.root
  const { k, projectId } = _scope(root, scope)
  _rememberThread(root, scope, projectId, root[k + 'activeSessionId'], !!inflight)
}

export const switchSession = async function (id, scope) {
  const el = this
  const root = el.state.root
  const { k, projectId, opts } = _scope(root, scope)
  if (!id || id === root[k + 'activeSessionId']) return
  // Same settle-on-leave as newSession — see the note there.
  try {
    el.call(
      'settleParkedAiToolApprovals',
      'The conversation was switched before this action was approved.'
    )
  } catch (_) {}
  shellSnapshot(this, root, k)
  const reset = {
    [k + 'chatTyping']: false,
    [k + 'currentAssistantId']: null,
    [k + 'showSessionMenu']: false
  }
  if (!k) {
    reset.chatInput = ''
    reset.pickedElements = []
  }
  // Server-backed path: load the conversation's real message history. On any
  // failure fall through to the local switch (reads {k}sessions[id].messages).
  const ai = _ai(el)
  if (ai) {
    // Scope the SDK BEFORE fetching. General threads need the active workspace set
    // on the AI service; switching can run before any send has set it (straight
    // after load, the user opens History and clicks an older thread), or after an
    // org switch left it stale. Without it getConversation hits _conversationBase()
    // with the wrong/no workspace → 404 → we'd silently fall back to the EMPTY
    // cached buffer = the "changing thread doesn't load" bug.
    pinWorkspace(ai, root, projectId)
    try {
      const data = await ai.getConversation(id, opts)
      const words = { continueWord: _continueWord(el) }
      const msgs = _creditsWorded(el, root, _toChatMessages(data && data.messages, words))
      // The thread the person opened may hold a SUSPENDED client call (the
      // page that ran its turn went away). Same read, same rule as
      // restoreActiveThread: an open link or a History click is a restore too.
      // Before this, the call stayed suspended on the server and its row read
      // as done (dev conversation 6ac35133…65db, 2026-10-05).
      const offer = pendingClientCallOffer(data && data.conversation, data && data.messages)
      root.update({ [k + 'activeSessionId']: id, [k + 'chatMessages']: msgs, ...reset })
      _rememberThread(root, scope, projectId, id, false)
      if (offer) {
        try {
          el.call('reofferPendingClientCall', {
            offer,
            conversationId: id,
            scope,
            opts,
            stateRoot: root,
            ai
          })
        } catch (_) {
          /* best-effort — the thread still opened; only the resume is lost */
        }
      }
      return
    } catch (err) {
      // Do NOT swallow — silently falling back to an empty buffer is exactly what
      // masked "previous thread won't load". Surface the reason, then fall through.
      if (typeof console !== 'undefined')
        console.debug('[simone] switchSession getConversation failed —', err && err.message)
    }
  }
  const sessions = root[k + 'sessions'] || {}
  const target = sessions[id]
  if (!target) return
  root.update({
    [k + 'activeSessionId']: id,
    [k + 'chatMessages']: target.messages || [],
    ...reset
  })
  _rememberThread(root, scope, projectId, id, false)
}

// Remove a thread from the person's history. Both entry points reach THIS
// function: History ⋯ "Remove" (sessionContextAction below) and the shared
// Sessions ▾ row × (shared SessionMenu → the shared dispatcher →
// shellDeleteSession). So the house confirm lives here (CRUD standard rule 3,
// D1): nothing changes before the person says Remove. The server soft-archives
// THEIR link to the conversation (members it was shared with keep it), so the
// row goes at once and comes back, with a visible error, when the server
// refuses — on origin/main c9d6dfbbd the row was dropped even then.
export const deleteSession = function (id) {
  const el = this
  const root = el.state.root
  if (!id) return
  const sess = (root.sessions || {})[id]
  const name = (sess && sess.title) || el.call('polyglot', 'workspace.simone.newChat')
  const removeNow = async () => {
    const before = root.sessions || {}
    const wasOpen = id === root.activeSessionId
    const openMessages = root.chatMessages
    const sessions = { ...before }
    delete sessions[id]
    let activeSessionId = root.activeSessionId
    let chatMessages = root.chatMessages
    if (wasOpen) {
      const remaining = Object.values(sessions).sort((a, b) => (b.ts || 0) - (a.ts || 0))
      if (remaining.length) {
        activeSessionId = remaining[0].id
        chatMessages = remaining[0].messages || []
      } else {
        // A NEW local thread, never null: a null id makes the next send fall
        // back to the SDK's cached "last used" thread — the one just deleted,
        // which the server answers with 404. An 's-…' id is sent as a new
        // conversation (the transport's newConversation).
        activeSessionId = newSessionId()
        chatMessages = []
      }
    }
    root.update({ sessions, activeSessionId, chatMessages })
    // Deleting the open thread must not leave a pointer at a dead conversation.
    _rememberThread(root, 'general', null, activeSessionId, false)
    // A local (`s-…`) thread has no server conversation: nothing to archive.
    const ai = _ai(el)
    if (!ai || String(id).startsWith('s-')) return
    pinWorkspace(ai, root, null)
    try {
      await ai.deleteConversation(id)
    } catch (err) {
      // The server kept it: the row comes back, and the open thread too when
      // the person has not moved to another one meanwhile.
      const restored = { ...(root.sessions || {}) }
      if (before[id]) restored[id] = before[id]
      const back = wasOpen && root.activeSessionId === activeSessionId
      root.update(
        back
          ? { sessions: restored, activeSessionId: id, chatMessages: openMessages }
          : { sessions: restored }
      )
      if (back) _rememberThread(root, 'general', null, id, false)
      el.call('openNotification', {
        type: 'error',
        title: el.call('polyglot', 'workspace.simone.removeFailed'),
        message: (err && err.message) || String(err)
      })
    }
  }
  el.call('confirmDialog', {
    title: el.call('polyglot', 'workspace.simone.removeTitle').replace('{name}', () => name),
    // The framework's own default message ("This action cannot be undone.",
    // en + ka) is the consequence here: the person cannot bring it back.
    confirmText: el.call('polyglot', 'workspace.simone.menu.remove'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: removeNow
  })
}

export const toggleSessionMenu = function () {
  const root = this.state.root
  root.update({ showSessionMenu: !root.showSessionMenu })
}

// Called by the transport every time chatMessages changes — keeps the
// active session entry in sync without explicit calls scattered around.
export const persistActiveSession = function (scope) {
  const root = this.state.root
  const { k } = _scope(root, scope)
  shellSnapshot(this, root, k)
}

// ── Autosave + thread management ───────────────────────────────────────────

// Called after a turn completes (simoneAiTransport onDone). The SDK has by now
// lazily created/updated the server conversation for this thread, so reload the
// server thread list — the just-used conversation shows in History immediately
// (autosave is visible without a reload) — and repoint a STILL-LOCAL (`s-…`)
// active session at its now-persisted server conversation (the most-recently-
// updated one) so the open thread is highlighted in History and the star/remove
// context actions operate on the real server id. The live chatMessages buffer is
// untouched — only the id pointer moves.
export const refreshSessionsAfterTurn = async function (scope) {
  const el = this
  const root = el.state.root
  if (!_ai(el)) return
  const { k } = _scope(root, scope)
  await hydrateServerSessions.call(el, scope)
  const cur = root[k + 'activeSessionId']
  if (cur && String(cur).startsWith('s-')) {
    const top = Object.values(root[k + 'sessions'] || {})
      .filter((v) => v && typeof v === 'object')
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))[0]
    if (top && top.id) root.update({ [k + 'activeSessionId']: top.id })
  }
  // The turn has settled and the id pointer is final — remember it, not
  // in-flight, so a reload from here restores a complete thread.
  const { projectId } = _scope(root, scope)
  _rememberThread(root, scope, projectId, root[k + 'activeSessionId'], false)
}

// Star / unstar a thread (server: WorkspaceMemberConversation.pinned, surfaced as
// `pinned` by listConversations). Optimistic local flip + best-effort server
// patch; reverts the local flag if the server rejects.
export const toggleStarSession = async function (id) {
  const el = this
  const root = el.state.root
  if (!id) return
  const sessions = { ...(root.sessions || {}) }
  const cur = sessions[id]
  if (!cur) return
  const next = !cur.pinned
  sessions[id] = { ...cur, pinned: next }
  root.update({ sessions })
  const ai = _ai(el)
  // Local (`s-…`) threads have no server conversation yet — keep the pin
  // local-only (no PATCH to 404 on, no revert). Server threads persist it.
  if (!ai || String(id).startsWith('s-')) return
  pinWorkspace(ai, root, null)
  try {
    await ai.patchConversation(id, { pinned: next })
  } catch (_) {
    const revert = { ...(root.sessions || {}) }
    if (revert[id]) {
      revert[id] = { ...revert[id], pinned: !next }
      root.update({ sessions: revert })
    }
  }
}

// Share a conversation with the workspace: flip the owner-controlled `shared`
// flag on the server (any workspace member can then READ it) and put its link
// on the clipboard. The link is `?aiConversation=<id>` — consumed at shell
// boot (app.js, next to `?aiChatSession=`): opens the drawer and loads the
// thread through the same server-backed switch the history menu uses. When
// the sharer is on a `/w/<org>/<ws>/` URL that prefix rides along, so the
// recipient lands in the RIGHT workspace regardless of their own default —
// the lesson of the `?node=` link that opened against the wrong workspace
// (2026-08-12).
export const shareSession = async function (id) {
  const el = this
  const root = el.state.root
  if (!id) return
  // A local (`s-…`) thread has no server conversation — nothing to share.
  if (String(id).startsWith('s-')) {
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', 'workspace.simone.notShareable'),
      message: el.call('polyglot', 'workspace.simone.notShareableBody')
    })
    return
  }
  const ai = _ai(el)
  if (!ai) return
  pinWorkspace(ai, root, null)
  try {
    await ai.patchConversation(id, { shared: true })
  } catch (err) {
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', 'workspace.simone.shareFailed'),
      message: (err && err.message) || el.call('polyglot', 'workspace.simone.shareFailedBody')
    })
    return
  }
  try {
    const m = window.location.pathname.match(/^\/w\/[^/]+\/[^/]+/)
    const prefix = m ? m[0] : ''
    const url = `${window.location.origin}${prefix}/?aiConversation=${encodeURIComponent(id)}`
    await navigator.clipboard.writeText(url)
    el.call('openNotification', {
      type: 'success',
      title: el.call('polyglot', 'workspace.simone.linkCopied'),
      message: el.call('polyglot', 'workspace.simone.linkCopiedBody')
    })
  } catch (err) {
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', 'workspace.simone.copyFailed'),
      message: (err && err.message) || String(err)
    })
  }
}

// ContextDropdown itemsFn for a History thread row — Star/Unstar + Remove. The
// star label flips on the thread's current pinned state.
export const sessionContextItems = function (root) {
  const dd = (root && root.contextDropdown) || {}
  const sess = (root.sessions || {})[dd.sessionId]
  const pinned = !!(sess && sess.pinned)
  return [
    {
      key: 'star',
      action: 'star',
      label: pinned ? 'workspace.simone.menu.unstar' : 'workspace.simone.menu.star'
    },
    { key: 'share', action: 'share', label: 'workspace.simone.menu.shareLink' },
    { key: 'remove', action: 'remove', label: 'workspace.simone.menu.remove', danger: true }
  ]
}

// ContextDropdown actionFn — dispatch + close for the History thread context menu.
export const sessionContextAction = function (action, item) {
  const root = this.state.root
  const dd = root.contextDropdown || {}
  const id = dd.sessionId
  root.update({ contextDropdown: null }, { preventFetch: true })
  if (!id) return
  if (action === 'star') {
    this.call('toggleStarSession', id)
    // Re-open History (the ContextDropdown's outside-click closed it) so the
    // star toggle is immediately visible on the row.
    root.update({ showSessionMenu: true })
  } else if (action === 'share') {
    this.call('shareSession', id)
  } else if (action === 'remove') {
    this.call('deleteSession', id)
  }
}
